import { isArray } from '@ember/array';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { and, not, or } from 'ember-truth-helpers';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type Plugin from 'garaje/models/plugin';
import type PluginInstall from 'garaje/models/plugin-install';
import type FeatureFlagsService from 'garaje/services/feature-flags';

interface PlatformPluginSettingsPanelSignature {
  Args: {
    adhocPlugins: string[];
    isEnterprisePlan: boolean;
    plugin: Plugin;
    pluginInstalls: PluginInstall[];
    onUpgrade: () => void;
  };
}

export default class PlatformPluginSettingsPanel extends Component<PlatformPluginSettingsPanelSignature> {
  @service declare featureFlags: FeatureFlagsService;

  get hasAdhocOverride(): boolean {
    return isArray(this.args.adhocPlugins) && this.args.adhocPlugins.includes(this.args.plugin.id);
  }

  get appStore(): boolean {
    return this.featureFlags.isEnabled('appStore');
  }

  get integration(): string | null {
    return this.isInstalled ? `${this.appStore ? '' : 'install-'}${this.pluginInstall?.id}` : null;
  }

  get integrationsRoute(): string {
    if (this.appStore) return 'apps.dynamic';
    return this.isInstalled ? 'integrations.enabled' : 'integrations';
  }

  get isInstalled(): boolean {
    return Boolean(this.pluginInstall && ['active', 'pending'].includes(this.pluginInstall.status));
  }

  get linkText(): string {
    return this.isInstalled ? 'Configure settings →' : 'Install →';
  }

  get slug(): string {
    if (!this.appStore) return '';
    return this.isInstalled ? 'installed' : this.args.plugin?.name?.toLowerCase().replace(/[^a-z0-9]/g, '-');
  }

  get hash(): Record<string, string | null> {
    return this.appStore
      ? { install_id: this.integration }
      : { scrollTo: this.scrollTo, integration: this.integration };
  }

  get pluginInstall(): PluginInstall | undefined {
    return this.args.pluginInstalls.find((pluginInstall) => {
      return pluginInstall.belongsTo('plugin').id() === this.args.plugin.id && pluginInstall.status !== 'uninstalled';
    });
  }

  get scrollTo(): string {
    return this.isInstalled ? `pluginInstall-${this.pluginInstall?.id}` : this.args.plugin.key;
  }

  <template>
    <SettingsPanel @isExpandable={{false}} ...attributes as |panel|>
      <panel.header @iconElement={{@plugin.iconSmall}} @title={{@plugin.name}} @pillText="Integration" as |header|>
        <header.description>
          {{@plugin.description}}
          {{#if (or @isEnterprisePlan this.hasAdhocOverride)}}
            {{#if this.appStore}}
              <LinkTo
                @route={{this.integrationsRoute}}
                @model={{this.slug}}
                @query={{this.hash}}
                class="whitespace-nowrap"
                data-test-link-to-integration
              >
                {{this.linkText}}
              </LinkTo>
            {{else}}
              <LinkTo
                @route={{this.integrationsRoute}}
                @query={{this.hash}}
                class="whitespace-nowrap"
                data-test-link-to-integration
              >
                {{this.linkText}}
              </LinkTo>
            {{/if}}
          {{/if}}
        </header.description>
        {{#if (and (not @isEnterprisePlan) (not this.hasAdhocOverride))}}
          <header.controls>
            <button
              data-test-platform-plugin-upgrade-button
              type="button"
              class="btn btn--small btn--brand"
              {{on "click" @onUpgrade}}
            >
              Upgrade
            </button>
          </header.controls>
        {{/if}}
      </panel.header>
    </SettingsPanel>
  </template>
}
